import React, { useEffect, useMemo, useState } from "react";

import { reportCmBreakdownMismatch, useTranslation } from "utils";

import type { ContributionMarginYearBreakdown } from "models";

import { KlimRadioGroup, Typography } from "components";

import { EveryLineTotalsTable } from "pages/app/scenario-analysis/components/financial-breakdown/every-line/EveryLineTotalsTable";
import { EveryLineYearTable } from "pages/app/scenario-analysis/components/financial-breakdown/every-line/EveryLineYearTable";
import { LineMarker } from "pages/app/scenario-analysis/components/financial-breakdown/every-line/LineMarker";
import { buildCmLineBreakdown } from "pages/app/scenario-analysis/utils/cmLines";

type EveryLineView = "totals" | "years";

// Keyed on the run results themselves: one report per result, not per recalculation of the same result or tab switch.
const reportedMismatches = new WeakMap<ContributionMarginYearBreakdown[], WeakSet<ContributionMarginYearBreakdown[]>>();

const reportMismatchOnce = (yearsA: ContributionMarginYearBreakdown[], yearsB: ContributionMarginYearBreakdown[]) => {
  const reportedAgainst = reportedMismatches.get(yearsA) ?? new WeakSet();
  if (reportedAgainst.has(yearsB)) return;
  reportedAgainst.add(yearsB);
  reportedMismatches.set(yearsA, reportedAgainst);
  reportCmBreakdownMismatch();
};

export type EveryLineProps = {
  /** The /run years of a, the scenario being compared, and of b, the one it is compared against. */
  yearsA: ContributionMarginYearBreakdown[];
  yearsB: ContributionMarginYearBreakdown[];
  nameA: string;
  nameB: string;
  mainCropLabelLookup: Record<string, string>;
  marketPricePct: number;
  costDevelopmentPct: number;
  dataTestId?: string;
};

/** The line-by-line breakdown behind the CM difference of a scenario pair, as N-year totals or each year. */
export const EveryLine = ({
  yearsA,
  yearsB,
  nameA,
  nameB,
  mainCropLabelLookup,
  marketPricePct,
  costDevelopmentPct,
  dataTestId = "every-line",
}: EveryLineProps) => {
  const { t } = useTranslation("scenarioAnalysis");
  const [view, setView] = useState<EveryLineView>("totals");
  const breakdown = useMemo(() => buildCmLineBreakdown(yearsA, yearsB), [yearsA, yearsB]);

  // Lines that do not add up to the CM (e.g. the API without the breakdown fields) must not be shown as if they did.
  useEffect(() => {
    if (!breakdown.isReconciled) reportMismatchOnce(yearsA, yearsB);
  }, [breakdown, yearsA, yearsB]);

  if (!breakdown.isReconciled) {
    return (
      <Typography variant="paragraph-s" color="grey-600" data-testid={`${dataTestId}-unavailable`}>
        {t("everyLine.unavailable")}
      </Typography>
    );
  }

  return (
    <section className="every-line" data-testid={dataTestId}>
      <div className="every-line-toolbar">
        <Typography variant="paragraph-s" color="grey-600">
          {t(view === "totals" ? "everyLine.hint" : "everyLine.yearsHint")}
        </Typography>
        <KlimRadioGroup
          value={view}
          onChange={(event) => setView(event.target.value as EveryLineView)}
          options={[
            { value: "totals", label: t("everyLine.view.totals", { count: breakdown.horizonYears }) },
            { value: "years", label: t("everyLine.view.years") },
          ]}
          optionType="button"
          buttonStyle="solid"
          type="tertiary"
          size="middle"
          dataTestId={`${dataTestId}-view`}
        />
      </div>

      {view === "totals" ? (
        <EveryLineTotalsTable
          breakdown={breakdown}
          nameA={nameA}
          nameB={nameB}
          marketPricePct={marketPricePct}
          costDevelopmentPct={costDevelopmentPct}
          dataTestId={dataTestId}
        />
      ) : (
        <EveryLineYearTable years={breakdown.years} nameA={nameA} nameB={nameB} mainCropLabelLookup={mainCropLabelLookup} dataTestId={dataTestId} />
      )}

      <Typography variant="paragraph-s" className="every-line-key" data-testid={`${dataTestId}-key`}>
        <span>{t("everyLine.key.farm")}</span>
        <span className="every-line-key-item">
          <LineMarker marker="model" />
          {t("everyLine.key.model")}
        </span>
        <span className="every-line-key-item">
          <LineMarker marker="trend" />
          {t("everyLine.key.trend")}
        </span>
      </Typography>
    </section>
  );
};

export default EveryLine;
